import { useEffect, useMemo, useState } from "preact/hooks";
import { t } from "../i18n";
import type { FilterState, StaticEvidenceProvider } from "../data/provider";
import { connectedIds, filterRadarEntities, filtersToParams, mostConnected, parseFilters, relatedSkillsCount } from "../data/filters";
import { track } from "../app/analytics";
import { hrefFor, navigate } from "../app/router";
import { loadLeadSession, loadPrefs, savePrefs } from "../app/storage";
import { GROUP_COLOR, GROUP_IDS, TrendRadar } from "../components/TrendRadar";
import { LeadCapture } from "../components/LeadCapture";
import { IconList, IconMap, IconSearch } from "../components/icons";
import { EntityPanel, RadarHelp } from "./EntityDrawer";
import type { RadarView } from "../data/provider";

function typeLabel(type: string): string {
  if (type === "technology") return t("radar.technologies");
  if (type === "skill") return t("radar.skills");
  if (type === "career") return t("radar.careers");
  if (type === "sector") return t("radar.sectors");
  return type;
}

function disciplineLabel(id: string): string {
  if (id === "biology") return t("radar.biology");
  if (id === "chemistry") return t("radar.chemistry");
  if (id === "interdisciplinary") return t("radar.interdisciplinary");
  return id;
}

export function RadarPage({ provider }: { provider: StaticEvidenceProvider }) {
  const [filters, setFilters] = useState<FilterState>(() => parseFilters(new URLSearchParams(window.location.search)));
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 900px)").matches);
  const [mode, setMode] = useState<"map" | "list">(() => {
    if (window.matchMedia("(max-width: 900px)").matches) return "list";
    return loadPrefs().radarView === "list" ? "list" : "map";
  });
  const [sortKey, setSortKey] = useState<"name" | "trend" | "type">("name");
  const [onboardingStep, setOnboardingStep] = useState(() => (loadLeadSession() ? 5 : 1));
  const [intent, setIntent] = useState<"trends" | "career-planning" | "competencies">("trends");

  useEffect(() => {
    track("tool_opened", { tool: "radar" });
    if (!loadLeadSession()) track("onboarding_started", { tool: "radar" });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const apply = () => {
      setMobile(mq.matches);
      if (mq.matches) setMode("list");
    };
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const params = filtersToParams(filters);
    const search = params.toString() ? `?${params}` : "";
    if (window.location.search !== search) navigate("/radar", search, true);
  }, [filters]);

  const entities = useMemo(() => provider.radarEntities(filters.view), [provider, filters.view]);
  const visible = useMemo(() => filterRadarEntities(entities, filters), [entities, filters]);
  const skillPool = useMemo(
    () =>
      filterRadarEntities(filters.view === "skill" ? entities : provider.radarEntities("skill"), {
        ...filters,
        view: "skill",
        trend: "all",
        q: "",
      }),
    [provider, filters, entities],
  );
  const sorted = useMemo(() => {
    return [...visible].sort((a, b) => {
      if (sortKey === "trend") return a.trendStatus.localeCompare(b.trendStatus);
      if (sortKey === "type") return a.type.localeCompare(b.type);
      return a.name.localeCompare(b.name);
    });
  }, [visible, sortKey]);

  const selected = visible.find((e) => e.id === filters.entity) ?? entities.find((e) => e.id === filters.entity);
  const connected = filters.connections && selected ? connectedIds(selected, visible) : new Set<string>();
  const topConnected = mostConnected(visible);
  const chartLabel = t("a11y.chart", {
    discipline: t(`radar.${filters.discipline === "all" ? "all" : filters.discipline}`),
    trend: filters.trend === "all" ? t("radar.all") : t(`radar.${filters.trend}`),
    type: t(
      `radar.${filters.view === "technology" ? "technologies" : filters.view === "skill" ? "skills" : filters.view === "career" ? "careers" : "sectors"}`,
    ),
    count: visible.length,
    connected: topConnected.length ? topConnected.map((e) => e.name).join(", ") : "",
  });

  const update = (patch: Partial<FilterState>) => {
    setFilters((f) => ({ ...f, ...patch }));
    track("radar_filter_changed", { view: patch.view ?? filters.view });
  };

  const selectEntity = (id: string) => {
    update({ entity: id, connections: true });
    track("radar_entity_opened", { id });
  };

  const openRelated = (id: string, type: RadarView) => {
    const related = provider.radarEntities(type).find((e) => e.id === id);
    const group: FilterState["group"] =
      type === "skill" && related?.groupId && GROUP_IDS.includes(related.groupId as (typeof GROUP_IDS)[number])
        ? (related.groupId as (typeof GROUP_IDS)[number])
        : "all";
    update({
      view: type,
      entity: id,
      connections: true,
      group,
    });
    track("radar_entity_opened", { id });
  };

  const dense = visible.length > 22;

  if (onboardingStep < 5) {
    return (
      <article class="obs-page obs-flow-app obs-radar-onboarding">
        <div class="obs-flow-topbar" aria-label={t("flow.radar.aria")}>
          <div>
            <p class="obs-flow-step">{t("flow.radar.title")}</p>
            <p class="obs-flow-count">{t("flow.radar.step", { n: onboardingStep })}</p>
          </div>
          <div class="obs-flow-progress" aria-hidden="true">
            <span style={{ width: `${onboardingStep * 25}%` }} />
          </div>
        </div>

        {onboardingStep === 1 ? (
          <section class="obs-flow-screen">
            <div class="obs-flow-screen__inner">
              <p class="obs-eyebrow">BOOSTSCIENCE OBSERVATORY</p>
              <h1>{t("flow.radar.hero")}</h1>
              <p class="obs-flow-copy">{t("flow.radar.hero.body")}</p>
              <div class="obs-flow-options obs-flow-options--3">
                {(["biology", "chemistry", "interdisciplinary"] as const).map((id) => (
                  <button
                    key={id}
                    class={`obs-flow-option ${filters.discipline === id ? "is-selected" : ""}`}
                    type="button"
                    aria-pressed={filters.discipline === id}
                    onClick={() => update({ discipline: id })}
                  >
                    <span>{t(`radar.${id}`)}</span>
                  </button>
                ))}
              </div>
              <div class="obs-flow-actions">
                <button class="obs-btn obs-btn-primary" type="button" disabled={filters.discipline === "all"} onClick={() => setOnboardingStep(2)}>
                  {t("match.continue")}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {onboardingStep === 2 ? (
          <section class="obs-flow-screen">
            <div class="obs-flow-screen__inner">
              <p class="obs-flow-step">{t("flow.radar.choose")}</p>
              <h2>{t("flow.radar.choose.q")}</h2>
              <div class="obs-flow-options obs-flow-options--3">
                {([
                  ["skill", t("radar.skills"), t("flow.radar.skills.body")],
                  ["technology", t("radar.technologies"), t("flow.radar.technologies.body")],
                  ["career", t("radar.careers"), t("flow.radar.careers.body")],
                ] as const).map(([id, label, body]) => (
                  <button
                    key={id}
                    class={`obs-flow-option ${filters.view === id ? "is-selected" : ""}`}
                    type="button"
                    aria-pressed={filters.view === id}
                    onClick={() => update({ view: id, entity: undefined, group: id === "skill" ? "all" : "all" })}
                  >
                    <strong>{label}</strong>
                    <small>{body}</small>
                  </button>
                ))}
              </div>
              <div class="obs-flow-actions">
                <button class="obs-btn obs-btn-secondary" type="button" onClick={() => setOnboardingStep(1)}>{t("match.back")}</button>
                <button class="obs-btn obs-btn-primary" type="button" onClick={() => setOnboardingStep(3)}>{t("match.continue")}</button>
              </div>
            </div>
          </section>
        ) : null}

        {onboardingStep === 3 ? (
          <section class="obs-flow-screen">
            <div class="obs-flow-screen__inner">
              <p class="obs-flow-step">{t("flow.radar.objective")}</p>
              <h2>{t("flow.radar.objective.q")}</h2>
              <div class="obs-flow-options">
                {([
                  ["trends", t("flow.radar.intent.trends")],
                  ["career-planning", t("flow.radar.intent.career")],
                  ["competencies", t("flow.radar.intent.skills")],
                ] as const).map(([id, label]) => (
                  <button
                    key={id}
                    class={`obs-flow-option ${intent === id ? "is-selected" : ""}`}
                    type="button"
                    aria-pressed={intent === id}
                    onClick={() => setIntent(id)}
                  >
                    <span>{label}</span>
                  </button>
                ))}
              </div>
              <div class="obs-flow-actions">
                <button class="obs-btn obs-btn-secondary" type="button" onClick={() => setOnboardingStep(2)}>{t("match.back")}</button>
                <button class="obs-btn obs-btn-primary" type="button" onClick={() => setOnboardingStep(4)}>{t("match.continue")}</button>
              </div>
            </div>
          </section>
        ) : null}

        {onboardingStep === 4 ? (
          <LeadCapture
            tool="radar"
            discipline={filters.discipline === "all" ? undefined : filters.discipline}
            interest={`${filters.view}:${intent}`}
            title={t("flow.radar.ready")}
            body={t("flow.radar.ready.body")}
            onComplete={() => setOnboardingStep(5)}
          />
        ) : null}
      </article>
    );
  }

  return (
    <article class="obs-page obs-radar-page">
      <p class="obs-eyebrow">{t("brand.observatory")}</p>
      <h1>{t("radar.title")}</h1>
      <p class="obs-lede">{t("radar.body")}</p>
      <div class="obs-toolbar">
        <div class="obs-toolbar-primary">
          <label class="obs-search-wrap">
            <span class="obs-sr">{t("radar.search")}</span>
            <IconSearch class="obs-search-icon" size={18} />
            <input
              class="obs-search"
              type="search"
              placeholder={t("radar.search")}
              value={filters.q}
              onInput={(e) => update({ q: e.currentTarget.value })}
              aria-label={t("radar.search")}
            />
          </label>
          <div class="obs-toggle" role="group" aria-label={t("radar.viewMode")}>
            <button
              type="button"
              aria-pressed={mode === "map"}
              onClick={() => {
                setMode("map");
                savePrefs({ radarView: "radar" });
              }}
            >
              <IconMap size={16} />
              {t("radar.viewRadar")}
            </button>
            <button
              type="button"
              aria-pressed={mode === "list"}
              onClick={() => {
                setMode("list");
                savePrefs({ radarView: "list" });
              }}
            >
              <IconList size={16} />
              {t("radar.viewList")}
            </button>
          </div>
        </div>
        <div class="obs-filters" role="search" aria-label={t("radar.filters")}>
          <div class="obs-seg" role="group" aria-label={t("radar.discipline")}>
            {(
              [
                ["all", t("radar.all")],
                ["biology", t("radar.biology")],
                ["chemistry", t("radar.chemistry")],
                ["interdisciplinary", t("radar.interdisciplinary")],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                aria-pressed={filters.discipline === id}
                onClick={() => update({ discipline: id })}
              >
                {label}
              </button>
            ))}
          </div>
          <div class="obs-seg" role="group" aria-label={t("radar.view")}>
            {(
              [
                ["technology", t("radar.technologies")],
                ["skill", t("radar.skills")],
                ["career", t("radar.careers")],
                ["sector", t("radar.sectors")],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                aria-pressed={filters.view === id}
                onClick={() =>
                  update({
                    view: id,
                    entity: undefined,
                    group: id === "skill" ? "scientific-lab" : "all",
                  })
                }
              >
                {label}
              </button>
            ))}
          </div>
          {filters.view === "skill" ? (
            <div class="obs-seg" role="group" aria-label={t("radar.group")}>
              {(
                [
                  ["all", t("radar.all")],
                  ...GROUP_IDS.map((id) => [id, t(`group.${id}`)] as const),
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={filters.group === id}
                  onClick={() => update({ group: id, entity: undefined })}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}
          <label class="obs-field">
            {t("radar.trend")}
            <select value={filters.trend} onChange={(e) => update({ trend: e.currentTarget.value as FilterState["trend"] })}>
              <option value="all">{t("radar.all")}</option>
              <option value="emerging">{t("radar.emerging")}</option>
              <option value="growing">{t("radar.growing")}</option>
              <option value="established">{t("radar.established")}</option>
              <option value="transforming">{t("radar.transforming")}</option>
            </select>
          </label>
          <label class="obs-field">
            {t("radar.geo")}
            <select
              value={filters.geography}
              onChange={(e) => update({ geography: e.currentTarget.value as FilterState["geography"] })}
            >
              <option value="all">{t("radar.geoAll")}</option>
              <option value="europe">{t("radar.europe")}</option>
              <option value="ukraine">{t("radar.ukraine")}</option>
            </select>
          </label>
          <button
            class="obs-btn obs-btn-secondary"
            type="button"
            onClick={() => setFilters(parseFilters(new URLSearchParams()))}
          >
            {t("radar.clear")}
          </button>
        </div>
      </div>
      <p class="obs-result-count" aria-live="polite">
        {t("radar.count", { n: visible.length })}
        {mobile && mode === "list" ? ` · ${t("radar.mobileList")}` : ""}
        {dense && mode === "map" ? ` · ${t("radar.dense")}` : ""}
      </p>
      <p class="obs-sr">{chartLabel}</p>
      <div class="obs-radar-layout">
        <div class="obs-radar-main">
          {visible.length === 0 ? (
            <div class="obs-empty">{filters.q ? t("radar.searchEmpty") : t("radar.empty")}</div>
          ) : mode === "map" ? (
            <div class="obs-radar-wrap">
              <p class="obs-radar-caption">{t("radar.ringGuide")}</p>
              <TrendRadar
                entities={sorted}
                selectedId={filters.entity}
                connected={connected}
                showLabels={!mobile && visible.length <= 22}
                onSelect={selectEntity}
              />
            </div>
          ) : (
            <div class="obs-table-wrap">
              <table class="obs-table">
                <thead>
                  <tr>
                    <th>
                      <button class="obs-linkish" type="button" onClick={() => setSortKey("name")}>
                        {t("radar.col.name")}
                      </button>
                    </th>
                    <th>
                      <button class="obs-linkish" type="button" onClick={() => setSortKey("type")}>
                        {t("radar.col.type")}
                      </button>
                    </th>
                    <th>{t("radar.col.discipline")}</th>
                    <th>
                      <button class="obs-linkish" type="button" onClick={() => setSortKey("trend")}>
                        {t("radar.col.trend")}
                      </button>
                    </th>
                    <th title={t("radar.col.skillsHint")}>{t("radar.col.skills")}</th>
                    <th>{t("radar.col.careers")}</th>
                    <th>{t("radar.col.source")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((e) => (
                    <tr
                      key={e.id}
                      class={e.id === filters.entity ? "is-selected" : undefined}
                      onClick={() => selectEntity(e.id)}
                    >
                      <td>
                        <button class="obs-linkish" type="button" onClick={() => selectEntity(e.id)}>
                          {e.name}
                        </button>
                      </td>
                      <td>{typeLabel(e.type)}</td>
                      <td>{e.disciplineIds.map(disciplineLabel).join(", ")}</td>
                      <td>
                        <span class={`obs-badge obs-badge-${e.trendStatus}`}>{t(`radar.${e.trendStatus}`)}</span>
                      </td>
                      <td>{relatedSkillsCount(e, skillPool)}</td>
                      <td>{e.relatedCareerIds.length}</td>
                      <td>{e.sourceIds.map((id) => provider.sourceById(id)?.name).filter(Boolean).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div class="obs-legend" aria-label={t("radar.filters")}>
            {Object.entries(GROUP_COLOR).map(([id, color]) => (
              <span key={id}>
                <i class="obs-swatch" style={{ background: color }} />
                {t(`group.${id}`)}
              </span>
            ))}
          </div>
        </div>
        <div class="obs-radar-side">
          {selected ? (
            mobile ? null : (
              <EntityPanel
                entity={selected}
                provider={provider}
                skillPool={skillPool}
                onClose={() => update({ entity: undefined })}
                onOpenRelated={openRelated}
                variant="side"
              />
            )
          ) : (
            <RadarHelp />
          )}
        </div>
      </div>
      {selected && mobile ? (
        <EntityPanel
          entity={selected}
          provider={provider}
          skillPool={skillPool}
          onClose={() => update({ entity: undefined })}
          onOpenRelated={openRelated}
          variant="sheet"
        />
      ) : null}
      <p class="obs-radar-foot">
        <a href={hrefFor("/skills-match")}>{t("radar.exploreMatch")}</a>
      </p>
    </article>
  );
}
